import { LitElement, html, css, nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref, Ref } from "lit/directives/ref.js";
import sharedStyle from "./shared-style.css";
import { emptyToUndefined, getUTMTagsFromURL } from "./utils";
import { creditCardIcon } from "./icons/credit-card";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { buildingIcon } from "./icons/building";
import { heartIcon } from "./icons/heart";

const nisUrl = "https://nis2.nahtamatudloomad.ee";
const sliderMaximumAmount = 200;

@customElement("donation-component")
// @ts-ignore - typescript doesn't like that this class is not used
class Donation extends LitElement {
  @property()
  donationTerms: string =
    "https://www.nahtamatudloomad.ee/annetamise-tingimused?nl-refer";

  @property()
  campaign: string;

  @property()
  defaultDonationType: "recurring" | "montonio" = "recurring";

  @property()
  enableDonationTypeSwitcher: string = "true";

  @property()
  amount: number = 25;

  donation: any;

  private error: string | undefined = undefined;
  private formRef: Ref<HTMLFormElement> = createRef();
  private bankRef: Ref<HTMLSelectElement> = createRef();
  private sliderRef: Ref<Slider> = createRef();
  private firstNameRef: Ref<HTMLInputElement> = createRef();
  private lastNameRef: Ref<HTMLInputElement> = createRef();
  private emailRef: Ref<HTMLInputElement> = createRef();
  private identificationNumberRef: Ref<HTMLInputElement> = createRef();
  private phoneRef: Ref<HTMLInputElement> = createRef();

  connectedCallback(): void {
    super.connectedCallback();

    this.donation = {
      type: this.defaultDonationType,
      form: {
        firstName: "" as string | undefined,
        lastName: "" as string | undefined,
        phone: "" as string | undefined,
        identificationNumber: "" as string | undefined,
        email: "" as string | undefined,
        bank: undefined as string | undefined,
      },
      redirectUrl: undefined as string | undefined,
    };
  }

  attributeChangedCallback(
    name: string,
    old: string | null,
    value: string | null
  ): void {
    if (name === "amount" && value !== null && !isNaN(parseFloat(value))) {
      const amount = parseFloat(value);
      this.amount = amount;
      this.sliderRef.value?.changeValue(amount);
    } else {
      super.attributeChangedCallback(name, old, value);
    }
  }

  static get styles() {
    return [
      unsafeCSS(sharedStyle),
      css`
        :host {
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 800px;
          justify-content: center;
          padding: 0 10px;
          box-sizing: border-box;
          justify-items: center;
          font-family: "Montserrat", sans-serif;
        }

        #donation-form {
          display: flex;
          flex-direction: column;
          width: 100%;
          margin: 10px 0 10px 0;
        }

        #donation {
          display: flex;
          flex-direction: column;
          justify-content: center;
          width: 100%;
          margin-top: 20px;
        }

        .terms {
          font-size: 16px;
          margin: 5px 0;
        }

        .terms:first-of-type {
          margin-top: 30px;
        }

        #other-amount {
          width: 50%;
          display: block;
          height: 30px;
          font-size: 16px;
          background: none;
          border: none;
          color: var(--black, black);
          padding: 5px 10px;
          box-sizing: border-box;
          transition: all 0.2s ease;
          font-family: "Montserrat", sans-serif;
          text-align: center;
          margin: 0 auto;
          margin-bottom: 10px;
        }

        slider-component {
          margin-top: 30px;
        }

        #bank-selection {
          display: grid;
          grid-template-columns: auto auto;
          grid-gap: 0 15px;
          margin: 15px 0;
          font-size: 19px;
          font-family: "Montserrat", sans-serif;
          background: var(--donation-main-color, rgba(76, 192, 181, 0.3));
          padding: 15px;
          align-items: center;
          border: 1px solid var(--donation-main-color, rgba(76, 192, 181, 0.5));
        }

        #bank-selection #bank {
          position: relative;
          height: 40px;
          border-radius: 0;
          border: none;
          text-transform: capitalize;
          color: var(--black, black);
          width: auto;
          background: white;
          text-align: left;
          padding: 0 15px;
          font-size: 16px;
          cursor: pointer;
        }

        #bank-selection label {
          font-family: "Montserrat", sans-serif;
          font-size: 16px;
          font-weight: normal;
        }

        #bank-selection option {
          font-family: "Montserrat", sans-serif;
          box-shadow: none;
          drop-shadow: none;
        }

        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        input[type="number"] {
          -moz-appearance: textfield;
        }

        .donation-buttons {
          display: inline-flex;
          grid-gap: 15px;
          align-self: center;
        }

        .donation-buttons button {
          font-size: 16px;
        }

        .icon {
          width: 24px;
          height: 24px;
          margin-right: 10px;
        }

        .table {
          display: table;
          margin-top: 30px;
        }

        .table-row {
          display: table-row;
          line-height: 2em;
        }

        .left {
          display: table-cell;
          font-weight: 600;
          text-align: right;
          width: 50%;
        }

        .right {
          display: table-cell;
          padding-left: 10px;
        }
      `,
    ];
  }

  donationContent() {
    return html`
      <section id="donation">
        ${(this.donation.type === "recurring" && this.bankSelection()) || null}
        <slider-component
          value="${this.amount}"
          ${ref(this.sliderRef)}
        ></slider-component>
        <input
          id="other-amount"
          @change="${this.changeOtherAmount}"
          placeholder="Muu summa"
        />
      </section>
    `;
  }

  bankSelection() {
    return html` <section id="bank-selection">
      <label>Vali pank: </label>
      <select name="bank" id="bank" ${ref(this.bankRef)}>
        <option value="swedbank">SWEDBANK</option>
        <option value="lhv">LHV</option>
        <option value="seb">SEB</option>
        <option value="luminor">LUMINOR</option>
        <option value="coop">COOP</option>
        <option value="citadele">CITADELE</option>
      </select>
    </section>`;
  }

  changeDonation(value: typeof this.donation) {
    Object.assign(this.donation, value);
    this.requestUpdate();
  }

  changeOtherAmount(event: InputEvent) {
    if (event.target !== null) {
      const target = event.target as HTMLInputElement;
      const val = parseInt(target.value);
      if (!isNaN(val) && val.toString() === target.value && val > 0) {
        this.amount = val;
        this.sliderRef.value?.changeValue(val);
      } else {
        target.value = "";
      }
    }
  }

  async submit(e: Event, method = "bank") {
    e.preventDefault();

    const form = this.formRef.value;
    if (form === undefined) {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.donation.form = {
      firstName: this.firstNameRef.value?.value,
      lastName: this.lastNameRef.value?.value,
      email: this.emailRef.value?.value,
      identificationNumber: this.identificationNumberRef.value?.value,
      phone: this.phoneRef.value?.value,
      bank: undefined,
    };

    const slider = this.sliderRef.value;
    if (slider !== undefined) {
      this.amount = slider.value;
    }

    const bank = this.bankRef.value;
    if (bank !== undefined) {
      this.donation.form.bank = bank.value;
    }

    // UTM tags from the URL
    const utmTags = getUTMTagsFromURL();

    // NIS2 request
    const donation = this.donation.form;
    const type = this.donation.type;
    const payload = {
      firstName: emptyToUndefined(donation.firstName),
      lastName: emptyToUndefined(donation.lastName),
      email: emptyToUndefined(donation.email),
      identificationNumber: emptyToUndefined(donation.identificationNumber),
      phone: emptyToUndefined(donation.phone),
      type: type,
      method: method,
      amount: Number(this.amount),
      bank:
        type === "recurring" && donation.bank !== undefined
          ? emptyToUndefined(donation.bank)
          : undefined,
      ...utmTags,
    };

    const response = await fetch(
      `${nisUrl}/api/donation/${this.campaign}/register`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    // Redirect to Montonio (one-off donation) or bank (recurring donation)
    if (response.ok) {
      this.donation.redirectUrl = await response.text();

      if (window.dataLayer) {
        window.dataLayer.push({ event: "donation" });
      }
      this.dispatchEvent(
        new CustomEvent("donation", { detail: this.donation })
      );
    } else {
      this.error = "Vabandame. Hetkel esineb süsteemis viga.";
      this.requestUpdate();
    }
  }

  render() {
    return html`
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap"
        rel="stylesheet"
      />

      ${
        this.enableDonationTypeSwitcher === "true"
          ? html` <div class="buttons">
              <button
                @click=${() =>
                  this.changeDonation({ ...this.donation, type: "recurring" })}
                class="tab-switcher ${this.donation.type === "recurring" &&
                "active"}"
              >
                Püsiannetus
              </button>

              <button
                @click=${() =>
                  this.changeDonation({ ...this.donation, type: "montonio" })}
                class="tab-switcher ${this.donation.type === "montonio" &&
                "active"}"
              >
                Ühekordne
              </button>
            </div>`
          : nothing
      }
      </div>

      ${this.donationContent()}

      <form
        ${ref(this.formRef)}
        @submit=${(e: Event) => this.submit(e)}
        id="donation-form"
      >
        <input
          placeholder="Eesnimi"
          id="firstname"
          class="input"
          type="text"
          ${ref(this.firstNameRef)}
        />
        <input
          placeholder="Perekonnanimi"
          id="lastname"
          class="input"
          type="text"
          ${ref(this.lastNameRef)}
        />
        <input
          placeholder="Telefon"
          id="phone"
          type="number"
          minlength="8"
          class="input"
          type="text"
          ${ref(this.phoneRef)}
        />
        <input
          placeholder="Isikukood"
          id="id"
          type="number"
          minlength="11"
          maxlength="11"
          class="input"
          ${ref(this.identificationNumberRef)}
        />
        <input
          placeholder="E-post"
          id="email"
          class="input"
          type="email"
          ${ref(this.emailRef)}
        />

        <label class="terms">
          <input type="checkbox" required />
          <span>
            Olen nõus
            <a href="${this.donationTerms}" target="_blank"
              >annetustingimustega</a
            >.
          </span>
        </label>
        <!-- <label class="terms"><input id="wantEmail" type="checkbox"/> Soovin saada igakuist ülevaadet loomade aitamisest. </label> -->
      </form>

      ${
        this.donation.type == "recurring"
          ? html`
              <button @click=${(e: Event) => this.submit(e)} class="submit">
                <span class="icon">${unsafeHTML(heartIcon)}</span>
                Teen püsiannetuse
              </button>
            `
          : html`
              <div class="donation-buttons">
                <button
                  @click=${(e: Event) => this.submit(e, "card")}
                  class="submit"
                >
                  <span class="icon">${unsafeHTML(creditCardIcon)}</span>
                  Annetan kaardiga
                </button>

                <button @click=${(e: Event) => this.submit(e)} class="submit">
                  <span class="icon">${unsafeHTML(buildingIcon)}</span>
                  Annetan pangast
                </button>
              </div>
            `
      }

      ${
        this.error !== undefined
          ? html`<div class="error-message">${this.error}</div>`
          : nothing
      }

      <div class="table">
        <div class="table-row">
          <div class="left">
            Saaja
          </div>
          <div class="right">
            MTÜ Nähtamatud Loomad
          </div>
        </div>
        <div class="table-row">
          <div class="left">
            Konto (Swedbank)
          </div>
          <div class="right">
            EE922200221066221071
          </div>
        </div>
      </div>
    `;
  }
}

@customElement("slider-component")
class Slider extends LitElement {
  @property()
  value: number;

  private valueRef: Ref<HTMLSpanElement> = createRef();

  static get styles() {
    return css`
      :host {
        width: 100%;
        position: relative;
        font-family: "Montserrat", sans-serif;
      }

      input[type="range"] {
        -webkit-appearance: none;
        margin: 10px 0;
        width: 100%;
        background: transparent;
      }

      input[type="range"]:focus {
        outline: none;
      }

      input[type="range"]::-webkit-slider-runnable-track {
        width: 100%;
        height: 6px;
        cursor: pointer;
        box-shadow: 0px 0px 0px var(--white, white), 0px 0px 0px #0d0d0d;
        background: var(--donation-main-color, rgba(76, 192, 181, 1));
        border-radius: 25px;
        border: 0px solid #000101;
      }

      input[type="range"]:focus::-webkit-slider-runnable-track {
        background: var(--donation-main-color, rgba(76, 192, 181, 1));
      }

      input[type="range"]::-moz-range-track {
        width: 100%;
        height: 6px;
        cursor: pointer;
        animate: 0.2s;
        box-shadow: 0px 0px 0px var(--white, white), 0px 0px 0px #0d0d0d;
        background: var(--donation-main-color, rgba(76, 192, 181, 1));
        border-radius: 25px;
        border: 0px solid #000101;
      }

      input[type="range"]::-moz-range-thumb {
        box-shadow: 0px 0px 0px var(--white, white), 0px 0px 0px #0d0d0d;
        border: 3px solid var(--white, white);
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background: var(--donation-main-color, rgba(76, 192, 181, 1));
        cursor: pointer;
      }

      input[type="range"]::-webkit-slider-thumb {
        box-shadow: 0px 0px 0px var(--white, white), 0px 0px 0px #0d0d0d;
        border: 3px solid var(--white, white);
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background: var(--donation-main-color, rgba(76, 192, 181, 1));
        cursor: pointer;
        -webkit-appearance: none;
        margin-top: -3.6px;
      }

      #value {
        font-size: 16px;
        background: var(--donation-main-color, rgba(76, 192, 181, 1));
        width: fit-content;
        padding: 0 10px;
        height: 30px;
        border-radius: 15px;
        text-align: center;
        display: flex;
        align-items: center;
        color: var(--white, white);
        font-weight: bold;
        position: absolute;
        top: -30px;
        transform: translate(-50%, 0);
      }
    `;
  }

  firstUpdated() {
    this.moveValue();
    this.requestUpdate();
  }

  sliderCallback(event: any) {
    const value = Math.max(
      1,
      Math.round(Math.abs(Number(event.target.value) / 5)) * 5
    );
    this.changeValue(value);
  }

  changeValue(value: number) {
    this.value = value;
    this.moveValue();
    this.requestUpdate();
  }

  moveValue() {
    const percentage = (this.value / sliderMaximumAmount) * 100;
    this.valueRef.value?.setAttribute("style", `left: ${percentage}% ;`);
  }

  render() {
    return html`
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap"
        rel="stylesheet"
      />
      <span id="value" ${ref(this.valueRef)}>
        €${this.value.toLocaleString("et")}
      </span>
      <input
        @input=${this.sliderCallback}
        type="range"
        step="5"
        max="${sliderMaximumAmount}"
        value=${Number(this.value)}
      />
    `;
  }
}
